import React, { useState } from 'react';
import styled from '@emotion/styled';

const Form = styled.form`
  display: grid;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: white;
  font-size: 0.7rem;
  text-transform: uppercase;
  font-family: 'Montserrat', sans-serif;
`;

const Input = styled.input`
  padding: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  background: white;
  color: black;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 0.8rem;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SubmitButton = styled(Button)`
  background: ${props => props.theme.colors.primary};
  color: black;
`;

const CancelButton = styled(Button)`
  background: #363636;
  color: white;
  border: 1px solid ${props => props.theme.colors.border};
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(var(--al-primary-rgb), 0.05);
  border-radius: 4px;
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 0.85rem;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--al-primary);
`;

function CustomerForm({ onSubmit, onCancel, initialData, submitButtonText = 'Add Customer' }) {
  const [formData, setFormData] = useState({
    client: initialData?.client || '',
    contact: initialData?.contact || '',
    email: initialData?.email || '',
    billingEmail: initialData?.billingEmail || '',
    phone: initialData?.phone || '',
    taxExempt: initialData?.taxExempt || false,
    receiveOrderEmails: initialData?.receiveOrderEmails !== false,
    receiveFollowUpEmails: initialData?.receiveFollowUpEmails !== false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup>
        <Label>Client *</Label>
        <Input
          type="text"
          name="client"
          value={formData.client}
          onChange={handleChange}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>Contact</Label>
        <Input
          type="text"
          name="contact"
          value={formData.contact}
          onChange={handleChange}
        />
      </FormGroup>

      <FormGroup>
        <Label>Email *</Label>
        <Input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>Billing Email (Optional)</Label>
        <Input
          type="email"
          name="billingEmail"
          value={formData.billingEmail}
          onChange={handleChange}
          placeholder="If different from main email"
        />
      </FormGroup>

      <FormGroup>
        <Label>Phone</Label>
        <Input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />
      </FormGroup>

      <CheckboxGroup>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            name="taxExempt"
            checked={formData.taxExempt}
            onChange={handleChange}
          />
          <span>Tax Exempt (Customer will not be charged sales tax)</span>
        </CheckboxLabel>
      </CheckboxGroup>

      <CheckboxGroup>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            name="receiveOrderEmails"
            checked={formData.receiveOrderEmails}
            onChange={handleChange}
          />
          <span>Receive Order Emails (order start, shirts arrived, all done)</span>
        </CheckboxLabel>
      </CheckboxGroup>

      <CheckboxGroup>
        <CheckboxLabel>
          <Checkbox
            type="checkbox"
            name="receiveFollowUpEmails"
            checked={formData.receiveFollowUpEmails}
            onChange={handleChange}
          />
          <span>Receive Follow-up Emails (review request, reorder reminder)</span>
        </CheckboxLabel>
      </CheckboxGroup>

      <ButtonGroup>
        <CancelButton type="button" onClick={onCancel}>
          Cancel
        </CancelButton>
        <SubmitButton type="submit">
          {submitButtonText}
        </SubmitButton>
      </ButtonGroup>
    </Form>
  );
}

export default CustomerForm; 